import type { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { HorizontalEdgePlacementNoExpr } from "building-types/entities/edgePlacementTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { ForeignEntityId } from "../handles/foreignEntityId";

export enum RoomPositionType {
  WALL = "WALL",
  REF_GEOM = "REF_GEOM",
  PROP = "PROP",
  CASEWORK = "CASEWORK",
}

export type RoomPosition = RoomWallPosition | RoomRefGeomPosition | RoomPropPosition | RoomCaseworkPosition;

export enum RoomWallPositionPlacementStyle {
  DEPRECATED = "DEPRECATED",
  VERSION_0 = "VERSION_0",
}

export interface RoomWallPosition extends HorizontalEdgePlacementNoExpr {
  readonly type: RoomPositionType.WALL;
  readonly lateralOffset: number;
  readonly lateralDatum?: WallLateralDatumAnchorAll;
  readonly lateralOffsetNormalizedToRoomWidth: boolean; //normalized to room width at param
  readonly placementStyle: RoomWallPositionPlacementStyle | undefined;
  readonly rotation?: number; // in degrees
}

export interface RoomRefGeomPosition {
  readonly type: RoomPositionType.REF_GEOM;
  readonly geomId?: ForeignEntityId<"REFERENCE_GEOM"> | null; // we can get more fancy with filtering refGeom later
  readonly edgeId: number | null;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly offset: number;
  readonly offsetNormalizedToRefGeomWidth: boolean; //normalized to refGeom width at param if ray intersects, offset to 0 if does not intersect
  readonly rotation?: number; // in degrees
}

export interface RoomPropPosition {
  readonly type: RoomPositionType.PROP;
  readonly propType: ForeignEntityId<"PROP">;
  readonly entityPlaneXOff: number;
  readonly entityPlaneXNormToRoomWidth: boolean; //normalized to room width at param
  readonly entityPlaneYOff: number;
  readonly entityPlaneYNormToRoomWidth: boolean; //normalized to room width at param
  readonly rotation?: number; // in degrees
}

export interface RoomCaseworkPosition {
  readonly type: RoomPositionType.CASEWORK;
  readonly caseworkName: string;
  readonly entityPlaneXOffset: number;
  readonly entityPlaneXNormalizedToRoomWidth: boolean; //normalized to room width at param
  readonly entityPlaneYOffset: number;
  readonly entityPlaneYNormalizedToRoomWidth: boolean; //normalized to room width at param
  readonly rotation?: number; // in degrees
}

export interface PositionWithLevel {
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly location: Vec2;
  readonly normal: Vec2;
}

export interface Position {
  readonly location: Vec2;
  readonly normal: Vec2;
}

export interface NormalizeEntityPointToPolyLine2Opts {
  readonly outline: PolyLine2;
  readonly bbx: number;
  readonly bby: number;
  readonly entityPlaneXOffset: number;
  readonly entityPlaneYOffset: number;
  readonly entityPlaneXNormalizedToRoomWidth: boolean;
  readonly entityPlaneYNormalizedToRoomWidth: boolean;
  readonly tx: number;
  readonly ty: number;
  readonly nx: number;
  readonly ny: number;
}

export interface NormalizeEntityPointToPolyLine2OptsForceXToRoomWidth {
  readonly outline: PolyLine2;
  readonly bbx: number;
  readonly bby: number;
  readonly entityPlaneXOffset: number;
  readonly entityPlaneYOffset: number;
  readonly entityPlaneXNormalizedToRoomWidth: boolean;
  readonly entityPlaneYNormalizedToRoomWidth: boolean;
  readonly tx: number;
  readonly ty: number;
  readonly nx: number;
  readonly ny: number;
  readonly reverse?: boolean;
}
